/**
 * انتخابِ عکسِ پروفایل — قرینه‌ی apps/mobile/src/native/imagePicker.ts.
 *
 * موبایل گالری/دوربینِ گوشی را با اجازه‌ی صریح باز می‌کند؛ مرورگر
 * `<input type="file">` خودش را دارد و اجازه‌ای جدا نمی‌خواهد — کاربر با
 * انتخابِ فایل، خودش اجازه داده.
 *
 * برش‌زدنِ مربعی که موبایل با `allowsEditing`/`aspect:[1,1]` انجام می‌دهد،
 * اینجا با یک کانواس شبیه‌سازی می‌شود: مربعِ مرکزیِ عکس، تا حداکثر ۲۵۶
 * پیکسل (آواتار حداکثر ۹۲ پیکسل نشان داده می‌شود؛ همان استدلالِ کیفیتِ
 * ۰.۵ موبایل — بزرگ‌تر فقط حجمِ آپلود را زیاد می‌کند).
 */
export type PickedImage =
  | { kind: 'ok'; base64: string }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

const MAX_SIZE = 256;
/** کاورِ رخداد تمامِ عرضِ کارت را می‌گیرد، پس بزرگ‌تر از آواتار بریده می‌شود. */
const MAX_WIDE = 1200;
/** عکسِ بعد از برنامه: بزرگ‌ترین‌شان، چون کاربر بازش می‌کند و نگاهش می‌کند. */
const MAX_PHOTO = 1600;

/**
 * برشِ مرکزیِ عکس با نسبتِ خواسته‌شده، تا سقفِ عرضِ داده‌شده.
 *
 * `ratio = 1` همان مربعِ آواتار است و `16/9` کاورِ رخداد — قرینه‌ی
 * `aspect` در انتخابگرِ موبایل.
 */
function cropBase64(img: HTMLImageElement, ratio: number, maxWidth: number): string {
  const natural = img.naturalWidth / img.naturalHeight;
  // عکسِ پهن‌تر از قاب از دو طرف بریده می‌شود، بلندتر از بالا و پایین.
  const sw = natural > ratio ? img.naturalHeight * ratio : img.naturalWidth;
  const sh = natural > ratio ? img.naturalHeight : img.naturalWidth / ratio;
  const sx = (img.naturalWidth - sw) / 2;
  const sy = (img.naturalHeight - sh) / 2;

  const outW = Math.min(sw, maxWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(outW);
  canvas.height = Math.round(outW / ratio);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  // فقط بخشِ بعد از کاما؛ آپلودِ core رشته‌ی base64ِ خام می‌خواهد.
  return canvas.toDataURL('image/jpeg', 0.85).split(',')[1] ?? '';
}

/**
 * کوچک‌کردنِ عکس **بدونِ برش** — نسبتِ خودش را نگه می‌دارد.
 *
 * قرینه‌ی `pickPhotoImage` موبایل که `allowsEditing` را خاموش می‌کند: عکسِ
 * خودِ آدم را اپ نباید ببرد. فقط سقفِ عرض/ارتفاع می‌گذارد تا آپلود سنگین
 * نشود (سقفِ باکت پنج مگابایت است، مایگریشن 0141).
 */
function scaleBase64(img: HTMLImageElement, maxSide: number): string {
  const side = Math.max(img.naturalWidth, img.naturalHeight);
  const k = side > maxSide ? maxSide / side : 1;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * k));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * k));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/jpeg', 0.85).split(',')[1] ?? '';
}

/**
 * یک فایلِ انتخاب‌شده → base64 آماده‌ی آپلود.
 *
 * جدا شده چون دو ورودی دارد و هر دو باید یک‌جور پردازش شوند: انتخاب از
 * `<input type=file>`، و **رها کردنِ فایل روی صفحه** (WHOSUP-213). اگر
 * دومی مسیرِ خودش را می‌ساخت، یک روز عکسِ کشیده‌شده کوچک نمی‌شد و
 * آپلودش از سقفِ باکت رد می‌کرد، بی‌آنکه انتخابِ عادی خراب باشد.
 */
function fileToBase64(
  file: File, ratio: number | null, maxWidth: number,
): Promise<PickedImage> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve({ kind: 'error', message: 'file read failed' });
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve({ kind: 'error', message: 'image decode failed' });
      img.onload = () => {
        try {
          resolve({
            kind: 'ok',
            base64: ratio === null
              ? scaleBase64(img, maxWidth)
              : cropBase64(img, ratio, maxWidth),
          });
        } catch (e) {
          resolve({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * یک `<input type=file>` مخفی می‌سازد، کلیک می‌کند، و منتظرِ انتخابِ کاربر
 * می‌ماند. `ratio` برابرِ `null` یعنی «نبُر، فقط کوچک کن».
 */
function pickImage(ratio: number | null, maxWidth: number): Promise<PickedImage> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) { resolve({ kind: 'cancelled' }); return; }
      fileToBase64(file, ratio, maxWidth).then(resolve);
    };

    // بعضی مرورگرها اگر input اصلاً attach نشده باشد، change را نمی‌فرستند.
    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('cancel', () => resolve({ kind: 'cancelled' }));
    input.click();
    // بعد از انتخاب یا لغو، دیگر لازم نیست بماند.
    setTimeout(() => input.remove(), 60_000);
  });
}

/**
 * چند فایلِ عکس → چند base64، به همان ترتیبی که آمده‌اند.
 *
 * فایلی که عکس نیست بی‌صدا کنار می‌رود: کاربری که یک پوشه را روی صفحه
 * می‌اندازد، «این یکی PDF بود» را نمی‌خواهد بشنود — می‌خواهد بقیه بروند.
 */
export async function filesToBase64(files: File[]): Promise<string[]> {
  const images = files.filter((f) => f.type.startsWith('image/'));
  const out: string[] = [];
  for (const f of images) {
    const r = await fileToBase64(f, null, MAX_PHOTO);
    if (r.kind === 'ok') out.push(r.base64);
  }
  return out;
}

/** انتخابِ چندتاییِ عکس — قرینه‌ی `pickPhotoImages` موبایل (WHOSUP-213). */
export function pickPhotoImages(): Promise<string[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;

    input.onchange = () => {
      filesToBase64([...(input.files ?? [])]).then(resolve);
    };

    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('cancel', () => resolve([]));
    input.click();
    setTimeout(() => input.remove(), 60_000);
  });
}

/** آواتار: مربعِ مرکزی، تا ۲۵۶ پیکسل. */
export const pickSquareImage = (): Promise<PickedImage> => pickImage(1, MAX_SIZE);

/** کاورِ رخداد: ۱۶:۹، تا ۱۲۰۰ پیکسل — قرینه‌ی `pickWideImage` در موبایل. */
export const pickWideImage = (): Promise<PickedImage> => pickImage(16 / 9, MAX_WIDE);

